import { defineStore } from 'pinia';
import { ref } from 'vue';

/**
 * 전역 알림(토스트) 스토어.
 *
 * 책임: 화면 우상단에 떠야 하는 일시 메시지 1개를 보관한다.
 * 출처 단일화(에러): API 응답 에러 메시지는 **오직 http.ts 인터셉터**가 `pushError()` 로 넣는다.
 * 컴포넌트 catch 에서 message.error/alert 를 직접 호출하지 않는다(중복 에러 알림 안티패턴 차단).
 * 성공(`pushSuccess`): 시스템이 사용자 대신 수행한 동작의 결과 확인용(예: 로그인 후 보류 담기 자동 완료).
 * 에러와 달리 컴포넌트가 직접 호출하는 게 정상 — 결과의 출처가 그 컴포넌트이기 때문이다.
 *
 * 표시는 App.vue 에 마운트된 단일 AppToast 컴포넌트가 담당 — 화면 어디서도 토스트를 직접 그리지 않는다.
 */

/** 토스트 종류 — error(인터셉터) / success(동작 결과 확인). */
export type ToastTone = 'error' | 'success';

/** 화면에 표시되는 토스트 1건. */
export interface Toast {
  /** 고유 키(렌더 key·dismiss 식별). */
  id: number;
  /** 시각 톤. */
  tone: ToastTone;
  /** 사용자 표시 메시지. */
  message: string;
}

/** 토스트 자동 소멸 시간(ms). */
const AUTO_DISMISS_MS = 4500;

export const useNotificationStore = defineStore('notification', () => {
  /** 현재 떠 있는 토스트 목록(최신이 위). */
  const toasts = ref<Toast[]>([]);

  /** id 시퀀스. */
  let seq = 0;
  /** id → 자동 소멸 타이머. dismiss 시 정리한다. */
  const timers = new Map<number, ReturnType<typeof setTimeout>>();

  /** 토스트 1건 제거 + 타이머 정리. */
  function dismiss(id: number): void {
    const timer = timers.get(id);
    if (timer != null) {
      clearTimeout(timer);
      timers.delete(id);
    }
    toasts.value = toasts.value.filter((t) => t.id !== id);
  }

  /** 토스트 1건 등록 + 자동 소멸 타이머 설정(톤 공통 로직). */
  function push(tone: ToastTone, message: string): void {
    const id = ++seq;
    toasts.value = [{ id, tone, message }, ...toasts.value];
    timers.set(
      id,
      setTimeout(() => dismiss(id), AUTO_DISMISS_MS),
    );
  }

  /**
   * 에러 토스트 추가 — http.ts 인터셉터 전용 진입점.
   * @param message 사용자 표시 메시지(인터셉터가 상태코드→문구로 매핑).
   */
  function pushError(message: string): void {
    push('error', message);
  }

  /**
   * 성공 토스트 추가 — 시스템이 사용자 대신 수행한 동작 결과 확인용.
   * @param message 사용자 표시 메시지(예: '연세대학교를 담았습니다').
   */
  function pushSuccess(message: string): void {
    push('success', message);
  }

  return { toasts, pushError, pushSuccess, dismiss };
});
